/* oxlint-disable react-hooks-js/immutability */
/* eslint-disable react-hooks/immutability */

import { useEffect, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { typographyVariants } from '@mint-lib/tw-components';
import { useSnapshot } from 'valtio';

import {
  SystemMetric,
  TemplateListItem,
  type TemplateListItemV2,
  type TemplatePreview,
} from '../../../api/types.js';
import { useImportHubV2 } from '../../../hooks/ff/useImportHubV2.js';
import {
  useTemplateMetrics,
  useTemplatePreview,
} from '../../../queries/index.js';
import { Input } from '../../atoms/Input.jsx';
import { HeaderOption, MetricAutocomplete } from '../MetricAutocomplete.jsx';
import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../TableComponents.jsx';
import type { TemplateDuplicationFormState } from './types.js';

type DataMappingSectionProps = {
  template: TemplateListItem;
  state: TemplateDuplicationFormState;
};

export const TemplateHeadersMappingTable = ({
  template,
  state,
}: DataMappingSectionProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const isImportHubV2 = useImportHubV2();

  const { data: templatePreview, isPending: isTemplatePreviewPending } =
    useTemplatePreview(template.id);

  const {
    data: { systemMetrics, customMetrics, maxMetricsCount },
    isPending: isMetricsPending,
  } = useTemplateMetrics(
    {
      granularityLevel: template.granularityLevel,
      activityId: isImportHubV2
        ? (template as TemplateListItemV2).activityId
        : undefined,
    },
    { throwOnError: true },
  );

  const isLoading = isTemplatePreviewPending || isMetricsPending;

  return (
    <>
      <div>
        <h4 className={typographyVariants({ variant: 'h4' })}>
          {t('Data mapping')}
        </h4>
        <span
          className={typographyVariants({
            variant: 'label01',
            color: 'secondary',
          })}
        >
          {t('The mapped headers with ARM items from the original template.')}
        </span>
      </div>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('File header')}</TableHeaderCell>
              <TableHeaderCell>{t('ARM items')}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <HeadersMappingSkeletonRows />
            ) : (
              <HeadersMappingBody
                templatePreview={templatePreview as TemplatePreview}
                systemMetrics={systemMetrics}
                customMetrics={customMetrics}
                maxMetricsCount={maxMetricsCount}
                state={state}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const HeadersMappingSkeletonRows = () =>
  Array.from({ length: 10 }, (_, rowIndex) => (
    <TableRow key={`template-headers-mapping-skeleton-${rowIndex}`}>
      <TableBodyCell isLoading />
      <TableBodyCell isLoading />
    </TableRow>
  ));

const HeadersMappingBody = ({
  templatePreview,
  systemMetrics,
  customMetrics,
  maxMetricsCount,
  state,
}: {
  templatePreview: TemplatePreview;
  systemMetrics: SystemMetric[];
  customMetrics: readonly SystemMetric[];
  state: TemplateDuplicationFormState;
  maxMetricsCount: number;
}) => {
  const snap = useSnapshot(state);

  const mappedHeadersCount = useMemo(
    () => snap.form.headers.filter((header) => header.mappedHeaderName).length,
    [snap.form.headers],
  );

  const isMetricsLimitReached =
    maxMetricsCount > 0 && mappedHeadersCount >= maxMetricsCount;

  const headerOptions = useMemo(() => {
    const hasCustomMetrics = customMetrics.length > 0;
    const customMetricsHeaderOptions = hasCustomMetrics
      ? customMetrics.map((customMetric) => ({
          ...customMetric,
          headerType: 'custom_metric' as const,
        }))
      : [{ headerType: 'noCustomMetrics' }];

    return [...systemMetrics, ...customMetricsHeaderOptions].map((option) => {
      const isSelected = Boolean(
        'name' in option &&
        state.form.headers.some(
          (header) => header.mappedHeaderName === option.name,
        ),
      );

      return {
        ...option,
        disabled: isSelected || isMetricsLimitReached,
      };
    }) as HeaderOption[];
  }, [systemMetrics, customMetrics, state.form.headers, isMetricsLimitReached]);

  useEffect(() => {
    state.form.headerRow = templatePreview.headerRow.map((v) => v);
    state.form.headers = templatePreview.headers.map((v) => ({ ...v }));
    state.form.headerOptions = headerOptions.map((v) => ({ ...v }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRawHeaderChange =
    (position: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const header = state.form.headers.find(
        (header) => header.columnPosition === position,
      );
      if (header) {
        header.rawHeader = e.target.value.trim();
      }
    };

  const selectHeaderOption = (position: number) => {
    return (_: unknown, value: HeaderOption | null) => {
      const templateHeader = state.form.headers.find(
        (header) => header.columnPosition === position,
      );
      if (templateHeader) {
        templateHeader.mappedHeaderName = value ? value.name : null;
        templateHeader.mappedHeaderLabel = value ? value.label : null;
      }
    };
  };

  const sortedHeaders = [...snap.form.headers].sort(
    (a, b) => a.columnPosition - b.columnPosition,
  );

  return sortedHeaders.map((header) => {
    const selectedOption = headerOptions.find(
      (headerOption) => headerOption.name === header.mappedHeaderName,
    );

    return (
      <TableRow key={header.columnPosition} hover tabIndex={-1}>
        <TableBodyCell>
          <Input
            defaultValue={header.rawHeader}
            onChange={handleRawHeaderChange(header.columnPosition)}
            size="small"
            autoComplete="off"
          />
        </TableBodyCell>
        <TableBodyCell className="min-w-[22.5rem]">
          <MetricAutocomplete
            defaultValue={selectedOption}
            options={headerOptions}
            onChange={selectHeaderOption(header.columnPosition)}
            maxMetricsCount={maxMetricsCount}
            isMetricsLimitReached={isMetricsLimitReached}
            granularityLevel={snap.form.granularityLevel}
          />
        </TableBodyCell>
      </TableRow>
    );
  });
};
